// @flow

import type { AppConfig } from '@icis/shared-header/dist/constants/types';
import ensureUrl from '../utils/ensureUrl';
import {
  BASE_URL,
  BFF_URL,
  IDS_URL,
  IDS_CLIENT_URL,
  IDS_TOKEN_AUDIENCE,
  ANALYTICS_SCRIPT,
  SEARCH_URL,
  ANALYTICS_URL,
  ANALYTICS_ACCOUNT,
  NEWS_URL,
  HOMEPAGE_V1_URL,
  HOMEPAGE_V2_URL,
  INTELLIGENCE_PAGES_NAV_URL,
  ACTIVITY_FEED_URL,
  ACTIVITY_FEED_BFF,
  LIVE_AGENT_SCRIPT_URL,
  LIVE_AGENT_CHAT_URL,
  LIVE_AGENT_CHAT_ID,
  LIVE_AGENT_DEPLOYMENT_ID,
  LIVE_AGENT_ORGANISATION_ID,
  LIVE_AGENT_OFFLINE_EMAIL,
  LIVE_AGENT_CHINA_CHAT_URL,
  LIVE_AGENT_CHINA_CHAT_ID,
  LIVE_AGENT_CHINA_DEPLOYMENT_ID,
  LIVE_AGENT_CHINA_ORGANISATION_ID,
  LIVE_AGENT_CHINA_OFFLINE_EMAIL,
  IDS_CLIENT_ID,
  OIDC_LOG_LEVEL,
  LOGGING_CONSOLE_CAPTURE_REG_EXP,
  AUTH_DOMAIN,
  WALKME_URL,
  OUTAGE_RETRY_INTERVAL,
  WALKME_WAIT_TIME, COMMODITIES_NAV_URL,
} from './environmentVariables';

let appConfig = null;

export type OidcConfig = {
  logLevel: number,
};

export type SSOConfig = {
  clientId: string,
};

export type Toggles = {
  useMockBff: boolean,
};

type Config = {
  sso: SSOConfig,
  oidc: OidcConfig,
} & AppConfig;

const initAppConfig = (): Config & { toggles: Toggles } => {
  if (!BASE_URL || !BFF_URL || !IDS_URL || !IDS_CLIENT_URL || !IDS_CLIENT_ID || !AUTH_DOMAIN) {
    const err = 'Cannot get config values from global config object.';
    throw new Error(err);
  }

  appConfig = {
    urls: {
      baseUrl: BASE_URL,
      bff: BFF_URL,
      identityServer: IDS_URL,
      client: IDS_CLIENT_URL,
      authDomain: AUTH_DOMAIN,
      idsTokenAudience: IDS_TOKEN_AUDIENCE,
      analyticsScript: ANALYTICS_SCRIPT,
      graphQL: `${ensureUrl(BFF_URL)}graphql`,
      supplyTracker: 'https://www.icis.com/dashboard/supplydisruptions?intcmp=subscriberlsdtcard',
      search: SEARCH_URL,
      analytics: ANALYTICS_URL,
      news: NEWS_URL,
      homepageV1Url: HOMEPAGE_V1_URL,
      homepageV2Url: HOMEPAGE_V2_URL,
      activityFeedUrl: ACTIVITY_FEED_URL,
      intelligencePagesNavUrl: INTELLIGENCE_PAGES_NAV_URL,
      commoditiesNavUrl: COMMODITIES_NAV_URL,
      activityFeedBff: `${ensureUrl(ACTIVITY_FEED_BFF)}graphql`,
    },
    liveAgent: {
      scriptUrl: LIVE_AGENT_SCRIPT_URL,
      en: {
        chatUrl: LIVE_AGENT_CHAT_URL,
        chatId: LIVE_AGENT_CHAT_ID,
        deploymentId: LIVE_AGENT_DEPLOYMENT_ID,
        organisationId: LIVE_AGENT_ORGANISATION_ID,
        offlineEmail:   LIVE_AGENT_OFFLINE_EMAIL,
      },
      zh: {
        chatUrl: LIVE_AGENT_CHINA_CHAT_URL,
        chatId: LIVE_AGENT_CHINA_CHAT_ID,
        deploymentId: LIVE_AGENT_CHINA_DEPLOYMENT_ID,
        organisationId: LIVE_AGENT_CHINA_ORGANISATION_ID,
        offlineEmail:   LIVE_AGENT_CHINA_OFFLINE_EMAIL,
      },
    },
    analytics: {
      account: ANALYTICS_ACCOUNT,
    },
    sso: {
      clientId: IDS_CLIENT_ID,
    },
    search: {
      minCharacters: 2,
    },
    features: {},
    oidc: {
      logLevel: parseInt(OIDC_LOG_LEVEL, 10),
    },
    logging: {
      consoleCaptureRegExp: LOGGING_CONSOLE_CAPTURE_REG_EXP,
    },
    walkMe: {
      url: WALKME_URL,
      waitTime: WALKME_WAIT_TIME,
    },
    toggles: {
      useMockBff: false,
    },
    outageRetryInterval: parseInt(OUTAGE_RETRY_INTERVAL, 10),
    defaultLocale: 'en',
  };
  return appConfig;
};

/**
 * Get an object containing config values
 *
 * @return {AppConfig} Object containing values read from root element
 */
const getAppConfig = () => {
  if (appConfig) return appConfig;
  return initAppConfig();
};

export default getAppConfig;
